import { FC, useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import classNames from 'classnames';
import {
	// fundInvestmentType,
	MAX_SESSION_NAME_LENGTH,
	sessionActionLine,
	sessionActionStatus,
} from './constant';
import {
	ComplexConfigStep,
	IOptionValue,
	IsQrInviteState,
	SIGN_OPTIONS,
	SelectedQRInstanceState,
	SelectedQRTemplateIdState,
	ShowSignOptionInQr,
	SignOptionState,
	HasMultiSignNodeState,
} from 'views/pipelines/components';
import { SelectedComplexPipelineNode } from 'global-stores';
import {
	IsMultiSignModalOpen,
	SignAgreementComplexStepConfigured,
	SelectedComplexSignAgreementStep,
	IsOverlayMultiSignModalOpen,
	// IsSessionChartLoadingState,
} from 'views/multi-sign-agreement';

import './tree-graph-chart.scss';
// import { labelData } from 'views/complex-onboarding-flow/constant';
import { UploadProofDocumentState } from 'views/upload-proof-document';
import { proofReadingKey } from 'views/pipelines/constants';
import { ComplexFlowUnconfigedState } from 'views/complex-onboarding-flow/stores/states';
import Tippy from '@tippyjs/react';
import {
	UploadDocumentOptionState,
	uploadDocumentOptionsValues,
} from 'views/multisign-template-selection';

import { useSignedDocuments } from './hooks';
import { ReactFlowCustomChart } from './react-flow-graph-chart';
import { reconcileBasicInfoGraph } from '../../reconcile-basic-info';
interface ISessionTreeGraph {
	details: any;
	showOnBoardingName?: boolean;
	viewComplexModal?: any;
	isInvite?: boolean;
	funds?: any[];
	docs?: any[];
	isSessionModal?: boolean;
	isSessionList?: boolean;
	runningNodeId?: string;
	/** Passed straight through to the chart — see react-flow-graph-chart. */
	stoppedNodeId?: string;
	stoppedForDays?: number;
	isStepper?: boolean;
	activeStepId?: string;
	selectedStepsState?: any[];
	maxNodes?: number;
	isRecipientInvite?: boolean;
	overlayTemplateIds?: string[];
}

/**
 * The accreditation tier a flow is configured for — a property of the FLOW's
 * `fullStatus`, not of any node or edge. It lives here because this component
 * holds the flow; the chart below is handed only the reconciled node list, so
 * it takes the answer as a prop rather than trying to find it.
 */
const ACCREDITATION_TIER: Record<string, string> = {
	'506b': '506b',
	'506c': '506c',
	investorChoice: 'investor choice',
};

/**
 * Two shapes carry this, because two different things are drawn here.
 *
 * A FLOW (onboarding list) states its tier in `fullStatus` — the flow is the
 * configuration, so the tier is a property of it.
 *
 * A SESSION (dashboard, sessions list) has no `fullStatus` at all. Its tier is
 * the setting the accreditation STEP was configured with, which rides on that
 * node: `pipelineActions[].metadata.accreditationType.value`. Reading only
 * `fullStatus` is why the tier appeared on the onboarding page and nowhere else.
 */

/**
 * WHERE AN APPLICANT STOPPED — derived here, once, for all three pages.
 *
 * Dashboard, Onboarding and the Sessions drawer all render this component with
 * only `details`, so deriving the frontier inside it is what makes the state
 * appear everywhere instead of needing the same rule written three times in
 * three decks — which is precisely how the accreditation label ended up with
 * three divergent definitions.
 *
 * A node is STOPPED when the session has visited it, has NOT moved in 24h, and
 * has not finished. That last clause matters: a completed or rejected session
 * did not stall, it ended. Callers may still pass `stoppedNodeId` explicitly to
 * override (ops-deck does, because it already computes the same frontier for
 * its `runningNodeId`).
 */
const DAY_MS = 24 * 60 * 60 * 1000;

const deriveStopped = (
	details: any
): {id?: string; days?: number} => {
	const visited = (details?.nodes ?? []).filter((n: any) => n?.visited);
	if (!visited.length) return {};

	const status = String(
		details?.status ?? details?.onboardingStatus ?? ''
	).toLowerCase();
	/* Terminal verdicts are not stalls. `completed`/`approved`/`rejected` all
	   mean the journey resolved; only an open session can be abandoned. */
	if (/complete|approved|rejected|declined/.test(status)) return {};

	const last = details?.updatedAt ?? details?.createdAt;
	const ms = last ? new Date(last).getTime() : NaN;
	if (!Number.isFinite(ms)) return {};

	const idle = Date.now() - ms;
	if (idle < DAY_MS) return {}; // still moving — that is `running`, not stopped

	/*
	 * WALK BACK TO THE DEEPEST *DRAWN* STEP — two ids are in play, and the
	 * frontier is frequently not on the canvas at all.
	 *
	 * The chart builds each react-flow node from `details.design.nodes[].id` —
	 * the DESIGN id space. A session node carries a fresh document `_id` plus
	 * `pipelineNodeId`, which is the design id. Comparing `_id` against the
	 * canvas therefore never matches; the codebase documents this same trap in
	 * `reconcile-basic-info.ts`.
	 *
	 * But swapping to `pipelineNodeId` alone is still not enough, and this is
	 * the part that kept the chip invisible: the last visited node is very often
	 * `aml`, which has NO drawn counterpart. AML runs as a shadow session node —
	 * in the design it is an action configured inside the KYC node
	 * (`pipelineActions[].metadata.amlVerification`), not a node of its own. So
	 * the true frontier can be a step that was never rendered, and highlighting
	 * it is impossible by construction.
	 *
	 * Walking backwards to the deepest step that IS drawn gives the honest
	 * answer: the furthest point of the journey a reader can actually see. For a
	 * session that died in AML that is KYC — which is where the operator would
	 * look anyway.
	 */
	const drawn = new Set(
		(details?.design?.nodes ?? []).map((n: any) => String(n?.id))
	);
	const days = Math.max(1, Math.floor(idle / DAY_MS));

	for (let i = visited.length - 1; i >= 0; i -= 1) {
		/* `?? _id` matters for pipeline-shaped `details` (the onboarding page),
		   where `_id` IS the canvas id. */
		const designId = visited[i]?.pipelineNodeId ?? visited[i]?._id;
		if (drawn.has(String(designId))) return {id: designId, days};
	}

	/* No design to check against — fall back to the naive frontier rather than
	   reporting nothing, so a shape we did not anticipate degrades to the old
	   behaviour instead of silently disabling the state. */
	if (!drawn.size) {
		const f = visited[visited.length - 1];
		return {id: f?.pipelineNodeId ?? f?._id, days};
	}
	return {};
};

/* THE TIER IS PER NODE, NOT PER FLOW -- the comment above was wrong about that.
   A flow may carry two accreditation steps at different levels (506(b) then
   506(c) is the case that surfaced it), and `fullStatus` lists BOTH, in node
   order. Picking the first and handing it to the chart as one string painted
   506b on every accreditation node, while the canvas, the publish payload and
   the stored node all said 506c for the second.

   So the answer is a map keyed by the chart's node id (`data.id`, the design
   id). Both shapes are covered:
   - a FLOW's `nodes[]._id` IS the design id; pipelines.api sets `type` on each
     accreditation node from its own `actions[0].metadata.accreditationType`.
   - a SESSION's `nodes[].pipelineNodeId` is the design id, and the level rides
     on `pipelineActions[].metadata.accreditationType.value`. */
const tiersOf = (details: any): Record<string, string> => {
	const tiers: Record<string, string> = {};
	(details?.nodes ?? []).forEach((n: any) => {
		if (n?.stepId !== 'accreditation') return;
		const actions = [...(n?.actions ?? []), ...(n?.pipelineActions ?? [])];
		const fromActions = actions
			.map((a: any) => {
				const t = a?.metadata?.accreditationType;
				return typeof t === 'string' ? t : t?.value;
			})
			.find((v: string) => v && ACCREDITATION_TIER[v]);
		const raw: string | undefined = ACCREDITATION_TIER[n?.type]
			? n.type
			: fromActions;
		const tier = raw ? ACCREDITATION_TIER[raw] : undefined;
		if (!tier) return;
		[n?._id, n?.pipelineNodeId, n?.id].forEach(id => {
			if (id) tiers[String(id)] = tier;
		});
	});
	return tiers;
};

/* Flow-level fallback for a payload that carries no node detail at all --
   the only case where the first `fullStatus` tier is still the best guess. */
const tierOf = (details: any): string | undefined => {
	const fromFlow = (details?.fullStatus ?? []).find(
		(k: string) => ACCREDITATION_TIER[k]
	);
	return fromFlow ? ACCREDITATION_TIER[fromFlow] : undefined;
};

const countNodeLength = 1;

export const SessionTreeGraph: FC<ISessionTreeGraph> = ({
	details,
	showOnBoardingName = false,
	isInvite = false,
	funds = [],
	docs = [],
	isSessionModal = false,
	isSessionList = false,
	runningNodeId,
	stoppedNodeId,
	stoppedForDays,
	viewComplexModal,
	isStepper = false,
	activeStepId,
	maxNodes,
	// isRecipientInvite = false,
	overlayTemplateIds = []
}) => {
	/* One derivation per render; the prop still wins when a caller has one. */
	const derivedStopped = useMemo(() => deriveStopped(details), [details]);

	const setComplexConfigStep = useSetRecoilState(ComplexConfigStep);
	const setComplexPipelineNode = useSetRecoilState(SelectedComplexPipelineNode);
	const QRInstancesForMultisign = useRecoilValue(SelectedQRInstanceState);
	const setQRTemplateId = useSetRecoilState(SelectedQRTemplateIdState);
	const activeOption = useRecoilValue<IOptionValue>(SignOptionState);
	const setIsMultiSignerModalOpen = useSetRecoilState(IsMultiSignModalOpen);
	const isQrFlow = useRecoilValue(IsQrInviteState);
	const setShowSignOptionVisible = useSetRecoilState(ShowSignOptionInQr);
	const setHasMultiSignNode = useSetRecoilState(HasMultiSignNodeState);
	const [signDocConfigStatus, setSignDocConfigStatus] = useRecoilState(SignAgreementComplexStepConfigured);
	// const isChartloading = useRecoilValue(IsSessionChartLoadingState);
	const setDocActiveOption = useSetRecoilState(UploadDocumentOptionState);
	const selectedSignAgreementId = useSetRecoilState(SelectedComplexSignAgreementStep);
	const uploadProofDocument = useRecoilValue(UploadProofDocumentState);
	const complexFlowUnconfigedState = useRecoilValue(ComplexFlowUnconfigedState);
	const setIsOverlayConfigOpen = useSetRecoilState(IsOverlayMultiSignModalOpen);

	const { checkMultipleSignAgreementInFlow } = useSignedDocuments();

	//constants
	const { proofVerification } = proofReadingKey;
	const isSessionNameLengthy =
		details?.onBoardingName?.length > MAX_SESSION_NAME_LENGTH ? true : false;

	// Paras : it still in development, backup purpose i just commented this code
	// const labelName = useCallback(
	// 	(id: string) => {
	// 		const nodeFiltered = (details?.design?.nodes || []).filter(
	// 			(item: any) => item?.id === id
	// 		);
	// 		// if node id not matched the check pipeline id to get details
	// 		const foundNode = details?.nodes?.find(
	// 			(item: any) => item?._id === id || item?.pipelineNodeId === id
	// 		);

	// 		const filtered = (details?.nodes || []).filter(
	// 			// Gaurav: In case fund detail _id is not specific chart node id, it's pipelineNodeId
	// 			(item: any) => item?._id === id || item?.pipelineNodeId === id
	// 		);

	// 		if (
	// 			filtered[0]?.label === 'Fund Investment' &&
	// 			fundInvestmentType?.[filtered?.[0]?.type]
	// 		) {
	// 			return `Fund (${fundInvestmentType[filtered[0]?.type]})`;
	// 		}
	// 		if (
	// 			filtered[0]?.label === 'Accreditation Verification' &&
	// 			filtered?.[0]?.type
	// 		) {
	// 			return `Accreditation (${filtered[0]?.type})`;
	// 		}
	// 		//Deepak : Change Sign Agreement label to Sign Doc. in case of complex
	// 		if (filtered[0]?.label === 'Sign Agreement') {
	// 			return 'Sign Doc.';
	// 		}

	// 		if (filtered[0]?.label === labelData.proofVerification) {
	// 			return actionKeyLabel.proofReading;
	// 		}

	// 		return (
	// 			filtered[0]?.label ||
	// 			nodeFiltered[0]?.label ||
	// 			(SESSION_CONFIGURE_KEYS_VALUE as Record<string, string>)[
	// 				foundNode?.stepId
	// 			] ||
	// 			'Loading...'
	// 		);
	// 	},
	// 	[details]
	// );

	// const getPricingData = useCallback((id: string)=>{
	// 	const nodeKey = (details?.design?.nodes || []).find(
	// 		(item: any) => item?.id === id
	// 	).stepId;
	// 	const nodePrice = selectedStepsState?.find(el => el.key === nodeKey)?.actions[0].price;
	// 	const type = nodeKey == 'signAgreement'? 'per packet' : 'per verification';
	// 	return `<div class="tree-node-pricing"><b>$${nodePrice}</b><br/>${type}</div>`

	// },[selectedStepsState, details])

	const actionLinkStatus = useCallback(
		(to: string, from: string) => {
			const toFiltered = (details?.nodes || []).filter(
				(item: any) => item.pipelineNodeId === to
			);
			const fromFiltered = (details?.nodes || []).filter(
				(item: any) => item.pipelineNodeId === from
			);

			return (fromFiltered[0]?.action?.status || 'pending') !== 'pending' &&
				sessionActionLine[toFiltered[0]?.action?.status] ===
					sessionActionLine[fromFiltered[0]?.action?.status]
				? '#0000FF'
				: '#B3B3B3';
		},
		[details?.nodes]
	);

	const getTrimedData = (nodes: any, edges: any, maxNodes: number) => {
		const trimedNode = nodes.slice(0, maxNodes);

		const validTrimedNode = trimedNode.map((el:any) => el['_id']);

		const filteredEdges = edges.filter(
		(edge: any) =>
			validTrimedNode.includes(edge.source) && validTrimedNode.includes(edge.target)
	);

	return { nodes: trimedNode, edges: filteredEdges };
}

	// Re-hydrate + chain-wire a legacy tier-0 Basic Info node (present in nodes[]
	// but missing from design.nodes) so this preview renders it like the builder.
	const reconciledDesign = useMemo(
		() =>
			reconcileBasicInfoGraph(
				details?.design?.nodes ?? [],
				details?.design?.edges ?? [],
				details?.nodes ?? []
			),
		[details]
	);

	const chartData = useMemo(() => {
		//@avinash: it was throwing typo error with $ sign so i have destructured it
		const { nodes, edges } = reconciledDesign;

		let n = structuredClone(nodes);
		let e = structuredClone(edges);

		if (typeof maxNodes === 'number') {
			const {nodes, edges} = getTrimedData(n, e, maxNodes);
			n = nodes;
			e = edges;
		}

		let data: any = [];
		if (e.length > 0) {
			data = (e.length ? e : [])?.map((item: any) => [
				item.source,
				item.target,
				actionLinkStatus(item.source, item.target),
			]);
		} else {
			const relationships: any[] = [];
			n?.forEach((node: { parentId: string[]; id: string }) => {
				if (node.parentId.length > 0) {
					node.parentId.forEach(pId => {
						relationships.push([pId, node.id, actionLinkStatus(pId, node.id)]);
					});
				} else {
					relationships.push([
						node.id,
						node.id,
						actionLinkStatus(node.id, node.id),
					]);
				}
			});

			data = relationships;
		}

		if (n.length === 0) {
			return [[reconciledDesign.nodes?.[0]?.id]];
		}
		return data;
	}, [actionLinkStatus, reconciledDesign, maxNodes]);

	const nodeColor = useMemo(() => {
		const data = (details?.nodes || []).map((item: any) => {
			const isConfiged = complexFlowUnconfigedState.find(
				el => el.id == item?.id
			)?.configed;

			const nodeStartColor =
				!item?.action?.status && item?.action?.stepStartTime;

			/*
			 * THE LIVENESS STEP IS COLOURED BY ITS VERDICT, NOT ITS STATUS.
			 *
			 * `action.status` on the authentication node means "the capture
			 * finished uploading" — pipelines writes `completed` the moment a
			 * selfie arrives and has no branch that writes anything else. Green
			 * therefore told a reviewer liveness had been established when
			 * nothing had judged it.
			 *
			 * The verdict is `action.data.verification.verdict`; only `passed`
			 * earns green. Anything else — including an older session with no
			 * verification at all, and the list payload, which returns
			 * `action` without its `data` — takes the amber "needs a human"
			 * colour rather than inheriting the old claim.
			 */
			const isAuthStep =
				(item?.stepId ?? item?.action?.stepId) === 'authentication' ||
				item?.action?.actionId === 'facialRecognition';
			const liveVerdict = item?.action?.data?.verification?.verdict;
			const unadjudicatedLiveness =
				isAuthStep &&
				item?.action?.status === 'completed' &&
				liveVerdict !== 'passed';

			let color = nodeStartColor
				? '#7935de'
				: unadjudicatedLiveness
				? sessionActionStatus[liveVerdict === 'failed' ? 'failed' : 'processing']
				: sessionActionStatus[item?.action?.status]
				? sessionActionStatus[item?.action?.status]
				: isStepper && activeStepId == item?.id
				? '#FFFFFF'
				: (isConfiged || isConfiged == undefined) && isStepper
				? '#389F75'
				: '#8c97b8';
			const pipelineNodeId = item?._id || item?.id;
			if (isInvite) {
				const isFundConfigured = !!funds.find(
					(fund: any) => fund.node === pipelineNodeId
				);

				const isDocConfigured = !!docs.find(
					(doc: any) => doc.node === pipelineNodeId
				);
				const { status } = signDocConfigStatus[pipelineNodeId] ?? {};
				const isMultiSignNodeConfigured = status === 'configured';

				const isProofDocConfigured = !!uploadProofDocument.find(
					(doc: any) => doc.node === pipelineNodeId
				);

				const isConfigured =
					isFundConfigured ||
					isDocConfigured ||
					isProofDocConfigured ||
					isMultiSignNodeConfigured;
				if (isConfigured) {
					color = sessionActionStatus.completed ?? color;
				}
			}
			return {
				id: item?.pipelineNodeId ?? pipelineNodeId,
				color,
				/* Published so the stylesheet can tell the two ambers apart. A
				   liveness capture MEASURED below the bar is a review item and
				   must pulse — that motion is how a reviewer finds it. One that
				   was never measured at all (every session captured before the
				   challenge was instrumented) is not pending anyone's decision;
				   it is a gap in the record, and pulsing it buries the real
				   ones under a list that blinks from top to bottom. */
				livenessVerdict: isAuthStep ? liveVerdict ?? 'unverified' : undefined,
				borderColor:
					isStepper && activeStepId == item?.id
						? '#0051CC'
						: isConfiged
						? '#389F75'
						: '#8c97b8',
			};
		});

		return data;
	}, [details?.nodes, complexFlowUnconfigedState, isStepper, activeStepId, isInvite, funds, docs, signDocConfigStatus, uploadProofDocument]);

	const onClick = useCallback(
		async (id: string) => {
			if (!isInvite) {
				return;
			}
			const selectNode = details?.nodes?.find((node: any) => node._id === id);
			const multipleSigningNodes = checkMultipleSignAgreementInFlow(details?.nodes, id)
			setHasMultiSignNode(multipleSigningNodes)
			if (!selectNode) {
				return;
			}
			setComplexPipelineNode(selectNode);
			if (
				selectNode?.stepId === 'signAgreement' &&
				!multipleSigningNodes &&
				isQrFlow
			) {
				const selectedNodeBeingConfigured =
					signDocConfigStatus[selectNode?._id];
				if (selectedNodeBeingConfigured?.status === 'configured') {
					setDocActiveOption(uploadDocumentOptionsValues.existing);
					setIsOverlayConfigOpen(true);
				} else {
					setShowSignOptionVisible(true);
					setComplexConfigStep({
						id,
						key: selectNode.key,
						actions: selectNode.actions,
					});
				}
				setSignDocConfigStatus(prev => {
					const prevState = structuredClone(prev);
					if (prevState[selectNode._id]?.status === 'configured') {
						return {
							...prevState,
							[selectNode._id]: {
								...prevState[selectNode._id],
								status: 'reconfigure',
							},
						};
					} else {
						return {
							...prevState,
							[selectNode._id]: {
								envelopeId: '',
								status: 'pending',
								templateId: '',
							},
						};
					}
				});
				selectedSignAgreementId(selectNode._id);
				return;
			}
			if (
				selectNode?.stepId === 'signAgreement' &&
				QRInstancesForMultisign?.length
			) {
				const templateData = QRInstancesForMultisign.find(item => item.node === id);
				if(templateData)
					setQRTemplateId(templateData?.templateId)
				selectedSignAgreementId(selectNode._id);
				// setIsComplexFlow(true);
				setIsMultiSignerModalOpen(true);
			}
			
			if (
				selectNode?.stepId === 'signAgreement' &&
				activeOption === SIGN_OPTIONS.COMMON
			) {
				/**
				 * mounting multisigner
				 * */
				setIsMultiSignerModalOpen(true);
				setSignDocConfigStatus(prev => {
					const prevState = structuredClone(prev);
					if (prevState[selectNode._id]?.status === 'configured') {
						return {
							...prevState,
							[selectNode._id]: {
								...prevState[selectNode._id],
								status: 'reconfigure',
							},
						};
					} else {
						return {
							...prevState,
							[selectNode._id]: {
								envelopeId: '',
								status: 'pending',
								templateId: '',
							},
						};
					}
				});
				selectedSignAgreementId(selectNode._id);
				return;
			}
			if (selectNode?.stepId === proofVerification) {
				setComplexPipelineNode(selectNode);
			}
			setComplexConfigStep({
				id,
				key: selectNode.key,
				actions: selectNode.actions,
			});
		},

		[
			activeOption,
			details?.nodes,
			isInvite,
			isQrFlow,
			proofVerification,
			setHasMultiSignNode,
			selectedSignAgreementId,
			setComplexConfigStep,
			setComplexPipelineNode,
			setIsMultiSignerModalOpen,
			setIsOverlayConfigOpen,
			setShowSignOptionVisible,
			setSignDocConfigStatus,
			signDocConfigStatus,
			setDocActiveOption,
			checkMultipleSignAgreementInFlow,
			QRInstancesForMultisign,
			setQRTemplateId
		]
	);

	const chartClasses = useMemo(() => {
		return classNames('onboarding-session-tree-graph', {
			'session-tree-graph': isSessionList,
			'onboarding-invite-tree-graph': isInvite || isSessionModal || isStepper,
			'onboarding-stepper-tree-graph': isStepper,
		});
	}, [isSessionList, isInvite, isSessionModal, isStepper]);

	return (
		<div className={chartClasses}>
			<ReactFlowCustomChart
				isSessionModal={isSessionModal}
				isSessionList={isSessionList}
				// per node id (see tiersOf); the single flow-level value is only the
				// fallback for a node the map does not know
				accreditationTiers={tiersOf(details)}
				accreditationTier={tierOf(details)}
				runningNodeId={runningNodeId}
				stoppedNodeId={stoppedNodeId ?? derivedStopped.id}
				stoppedForDays={stoppedForDays ?? derivedStopped.days}
				isInvite={isInvite}
				isStepper={isStepper}
				onClick={onClick}
				nodeColor={nodeColor}
				chartData={chartData}
				details={reconciledDesign.nodes}
				overlayTemplateIds={overlayTemplateIds}
			/>
			{!isStepper &&
				!isInvite &&
				!isSessionModal &&
				chartData.length > countNodeLength && (
					<div
						className="tree-graph-show-more"
						onClick={e => viewComplexModal(e, details)}
					>
						View <div>complete</div> flow
					</div>
				)}
			{showOnBoardingName && (
				<>
					{isSessionNameLengthy && (
						<Tippy content={details?.onBoardingName}>
							<div className="tree-graph-onboarding-name">
								{details?.onBoardingName}
							</div>
						</Tippy>
					)}
					{!isSessionNameLengthy && (
						<div className="tree-graph-onboarding-name ">
							{details?.onBoardingName}
						</div>
					)}
				</>
			)}
		</div>
	);
};
